import { Button, Note, ProgressSpinner } from "@appretio/ui-kit";
import type { ReactNode } from "react";
import * as styles from "./PageFrame.css";

export interface PageFrameProps {
  /** Page title; matches the page's side-menu label. */
  title: string;
  /** One line under the title. Longer explanations go in a Note. */
  description: string;
  /** The page's one primary action, right of the title. */
  action?: ReactNode;
  /** Shown in place of the blocks while the page's data loads or after it failed to. */
  loadState?: "loading" | "failed";
  /** Loads the page's data again; offered when `loadState` is "failed". */
  onRetry?: () => void;
  /** What a failed load means for this page. */
  failureText?: string;
  /** The page's blocks: toolbars, sections, notes. */
  children?: ReactNode;
}

/** One side-menu page: title, description, action, then its blocks. */
export function PageFrame({
  title,
  description,
  action,
  loadState,
  onRetry,
  failureText = "This page didn't load.",
  children
}: PageFrameProps) {
  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.description}>{description}</p>
        </div>
        {!loadState && action}
      </div>
      {loadState === "loading" && (
        <div className={styles.spinner}>
          <ProgressSpinner />
        </div>
      )}
      {loadState === "failed" && (
        <div className={styles.loadError}>
          <Note variant="error" size="compact" text={failureText} />
          {onRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      )}
      {!loadState && children}
    </section>
  );
}
